import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BRAND, API, CATEGORIES } from "../lib/brand";
import { ArrowRight, Sparkles, Shield, Headphones } from "lucide-react";
import ArtCard from "../components/ArtCard";

export default function Landing() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setFeatured((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page" className="bg-[#050605]">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={BRAND.heroRedwood}
            alt="Redwood forest with clover"
            className="absolute inset-0 w-full h-full object-cover"
            draggable="false"
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-24 pt-40 w-full">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-px bg-[#279153]" />
              <span className="text-[11px] tracking-[0.35em] uppercase text-[#95A89C]">
                Fáilte · An Irish Atelier
              </span>
            </div>
            <h1 data-testid="hero-title" className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95] mb-6">
              Where redwoods meet<br />
              <em className="text-[#279153] not-italic font-medium">clover</em> & song.
            </h1>
            <p data-testid="hero-tagline" className="text-base sm:text-lg text-[#95A89C] max-w-xl leading-relaxed mb-10">
              Grá — Irish for love — is an independent studio of music, photography
              and visual artwork. Every piece is watermarked, hand-released, and
              available to purchase directly from the artist.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/music"
                data-testid="hero-cta-explore"
                className="inline-flex items-center gap-3 px-7 py-4 bg-[#1E7040] text-white text-xs tracking-[0.3em] uppercase hover:bg-[#165932] transition-colors"
              >
                Explore the collection <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-story"
                className="inline-flex items-center gap-3 px-7 py-4 border border-white/30 text-white text-xs tracking-[0.3em] uppercase hover:border-[#279153] hover:text-[#279153] transition-colors"
              >
                The story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section data-testid="categories-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#5C7064] mb-3">Three doorways</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white">Music · Photography · Artwork</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {CATEGORIES.map((c, i) => (
            <Link
              key={c.slug}
              to={c.slug === "drawing" ? "/drawings" : `/${c.slug}`}
              data-testid={`category-${c.slug}`}
              className="relative group border border-[#1A2B20] bg-[#09120C] aspect-[4/5] overflow-hidden"
            >
              <div className="absolute inset-0 flex flex-col justify-end p-8 z-10">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#279153] mb-2">0{i + 1}</div>
                <h3 className="font-serif-display text-3xl text-white mb-2">{c.label}</h3>
                <p className="text-sm text-[#95A89C]">{c.short}</p>
                <div className="mt-6 flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-white group-hover:text-[#279153] transition-colors">
                  Enter <ArrowRight className="w-4 h-4" />
                </div>
              </div>
              <img
                src={`${API}/brand/knot.png`}
                alt=""
                aria-hidden="true"
                className="absolute -right-12 -bottom-12 w-72 h-72 object-contain opacity-20 group-hover:opacity-30 group-hover:rotate-6 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#09120C] via-[#09120C]/80 to-transparent" />
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section data-testid="featured-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-[11px] tracking-[0.35em] uppercase text-[#5C7064] mb-3">Recent releases</div>
              <h2 className="font-serif-display text-4xl text-white">From the studio</h2>
            </div>
            <Link to="/music" className="text-xs tracking-[0.3em] uppercase text-[#95A89C] hover:text-[#279153] hidden sm:inline-flex items-center gap-2">
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => <ArtCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* MANIFESTO */}
      <section data-testid="manifesto-section" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={BRAND.cloverTexture} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050605] via-[#050605]/90 to-[#050605]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-12">
          {[
            { icon: Shield, title: "Watermarked", body: "Every preview carries the Grá Celtic-knot watermark. Originals are protected." },
            { icon: Headphones, title: "Direct from artist", body: "No middlemen. Music, prints and drawings come straight from the studio to you." },
            { icon: Sparkles, title: "Irish roots", body: "From clover to redwood, the work draws on a love of land, story and song." },
          ].map((b) => (
            <div key={b.title} className="border-t border-[#1A2B20] pt-8">
              <b.icon className="w-6 h-6 text-[#279153] mb-4" />
              <h3 className="font-serif-display text-2xl text-white mb-3">{b.title}</h3>
              <p className="text-sm text-[#95A89C] leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
